import React, { useState, useEffect } from 'react';
import { useTheme } from './ThemeContext.tsx';
import { apiService, TokenResponse } from './services/api.ts';
import logo from './assets/Logo_grippy_opt.png'; // Adjust the path as necessary
import hvnLogo from './assets/HNV_Logo_light.png'; // Heineken Green Logo


type LoginProps = {
  onLogin: () => void;
};

const LogIn: React.FC<LoginProps> = ({ onLogin }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const { colors } = useTheme();

  // Check for authentication callback on component mount
  useEffect(() => {
    const handleAuthCallback = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const state = urlParams.get('state');

      if (code) {
        setIsLoading(true);
        setError(null);

        try {
          // Exchange authorization code for access token
          const tokenResponse: TokenResponse = await apiService.exchangeCodeForToken(code, state || undefined);

          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);

          // Call onLogin callback
          onLogin();
        } catch (error) {
          console.error('Authentication error:', error);
          setError('Authentication failed. Please try again.');
        } finally {
          setIsLoading(false);
        }
      }
    };

    handleAuthCallback();
  }, [onLogin]);

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Get Microsoft authentication URL from backend
      const authResponse = await apiService.getAuthUrl();

      // Redirect to Microsoft login
      window.location.href = authResponse.auth_url;
    } catch (error) {
      console.error('Error initiating Microsoft login:', error);
      setError('Failed to initiate Microsoft login. Please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={`flex flex-col h-screen w-screen ${colors.bg.primary} items-center p-4`}>

        {/* Header - Constrained Width */}
        <div className="w-full max-w-md">
          <div className="text-center mb-8">
            <img src={hvnLogo} alt="Heineken Green Logo" className="mx-auto mb-4" style={{ height: '64px' }} />
            <img src={logo} alt="Grippy AI Logo" className="mx-auto mb-4" style={{ height: '64px' }} />
          </div>
        </div>

        {/* Main Card - Constrained Width */}
        <div className="w-full max-w-md">
          <div className={`${colors.bg.secondary} rounded-xl ${colors.border.primary} border p-8 shadow-2xl`}>
            {/* Welcome Message */}
            <div className="text-center mb-8">
              <h2 className={`text-xl font-semibold ${colors.text.primary} mb-2`}>Welcome Back</h2>
              <p className={`${colors.text.secondary} text-sm`}>
                Use your Microsoft account to continue
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            {/* Microsoft Login Button */}
            <button
              onClick={handleMicrosoftLogin}
              disabled={isLoading}
              className={`w-full ${colors.interactive.primary} ${colors.interactive.primaryHover} disabled:opacity-60 disabled:cursor-not-allowed ${colors.text.inverse} font-semibold py-4 px-6 rounded-lg transition-all duration-200 flex items-center justify-center gap-3 border border-blue-500`}
            >
              {isLoading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </>
              ) : (
                <>
                  {/* Microsoft Logo */}
                  <svg width="20" height="20" viewBox="0 0 23 23" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <rect width="11" height="11" fill="#F25022" />
                    <rect x="12" width="11" height="11" fill="#7FBA00" />
                    <rect y="12" width="11" height="11" fill="#00A4EF" />
                    <rect x="12" y="12" width="11" height="11" fill="#FFB900" />
                  </svg>
                  <span>Sign in with Microsoft</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Security Notice - Constrained Width */}
        <div className="w-full max-w-md">
          <div className={`mt-6 mb-6 p-4 ${colors.bg.accent} rounded-lg border ${colors.border.primary}`}>
            <div className="flex items-center gap-2 mb-2">
              <svg className="w-4 h-4 text-blue-400 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
              </svg>
              <span className={`text-md font-bold ${colors.text.primary}`}>Secure Authentication</span>
            </div>
            <p className={`text-sm font-medium ${colors.text.secondary} leading-relaxed`}>
              Your login is secured through Microsoft's enterprise authentication system.
              We don't store your credentials locally.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default LogIn;